import moment from 'moment';
import React from 'react';
import {
  FieldValueType,
  GeneralInformationValueType,
  ItemModelValueType,
  ItemValueType,
  ReportConfig,
  ReportTag,
  ReportValue,
  SectionValueType,
  UseOfForceValueType
} from '../../../../../Models';
import {
  BystanderValidator,
  getBystanderValidator,
  getSameContactReasonValidator,
  getUniqueDriverValidator,
  getUOFValidator,
  getUseOfForceDefaultValue,
  isBystanderValid,
  isSameContactReasonValid,
  isUniqueDriverValid,
  isUOFValid,
  SameContactReasonValidator,
  UniqueDriverValidator,
  UOFValidator
} from '../../../../../Helpers';
import {
  Customer,
  CustomerContext,
  isEqualObject,
  LocationAreaType,
  Tour,
  TourContext
} from 'ui-smartforce-settings';
import { StateMandatedReportContext } from '../../../Main';
import { StateConfigContext } from '../../../../../Context/StateConfigContext';
import { ItemListSectionMessage } from '../ItemListSection/ItemListSection';
import {
  BYSTANDER_INVALID,
  BYSTANDER_NEEDS_UOF_CITIZEN,
  BYSTANDER_NO_ACTION,
  CITIZENS_MIN_MSG,
  CITIZENS_UOF_MIN_MSG,
  OFFICERS_NEEDS_CITIZEN_MSG,
  OFFICERS_NEEDS_UOF_CITIZEN_MSG,
  SAME_CONTACT_REASON_INVALID,
  SAME_CONTACT_REASON_LPR_NOTIFICATION_INVALID,
  UNIQUE_DRIVER_INVALID
} from '../../../../../Constants/Messages';
import {
  checkFormValid,
  getDateTime,
  getGeneralFormInitialValue,
  initGeneralInfoFromReport,
  isUseOfForceEmpty
} from '../Helpers';

export interface UseContactFormViewParams {
  caseNumber?: string;
  report?: ReportValue;
  isDraft?: boolean;
  generalFormAreas?: LocationAreaType[];
  onSubmit: (contactValue: ReportValue, isTour?: boolean) => void;
  onCleanUp?: (contactValue: ReportValue) => void;
}

export interface UseContactFormViewResult {
  showStateMandated: boolean;
  isAmend: boolean;
  isMissingInfoModalOpen: boolean;
  isSubmitDisabled: boolean;
  citizens: ItemValueType[];
  officers: ItemValueType[];
  complaints: ItemValueType[];
  investigations: ItemValueType[];
  administrative: ItemValueType[];
  generalForm: GeneralInformationValueType;
  useOfForceForm: UseOfForceValueType | undefined;
  agencyTags: ReportTag[] | undefined;
  stateConfig: ReportConfig;
  validators: {
    uof: UOFValidator;
    bystander: BystanderValidator;
    sameContactReason: SameContactReasonValidator;
    uniqueDriver: UniqueDriverValidator;
  };
  tour: Tour | undefined;
  getMissingInfoMessages: () => string[];
  getCitizensMessage: () => ItemListSectionMessage | undefined;
  getOfficersMessage: () => ItemListSectionMessage | undefined;
  onCitizensChange: (newCitizens: ItemValueType[], index?: number) => void;
  onContactFieldChange: (
    name: string,
    value: FieldValueType | SectionValueType,
    isStateRelated: boolean
  ) => void;
  onUseOfForceChange: (newUseOfForce: UseOfForceValueType) => void;
  onOfficersChange: (newOfficers: ItemValueType[]) => void;
  onComplaintsChange: (newComplaints: ItemValueType[]) => void;
  onInvestigationsChange: (newInvestigations: ItemValueType[]) => void;
  onAdministrativeChange: (newAdministrative: ItemValueType[]) => void;
  onTagsChange: (newTags: ReportTag[]) => void;
  onClickSubmit: () => void;
  onMissingInformationModalClose: () => void;
}

export const useContactFormView = ({
  caseNumber,
  report,
  isDraft,
  generalFormAreas,
  onSubmit,
  onCleanUp
}: UseContactFormViewParams): UseContactFormViewResult => {
  const {
    tour,
    status: tourStatus,
    onNext: onTourNext,
    onEnd: onTourEnd
  } = React.useContext(TourContext);

  const customer = React.useContext(CustomerContext).customer as Customer;
  const showStateMandated: boolean = React.useContext(
    StateMandatedReportContext
  );
  const stateName = customer.state_name;

  const stateConfig: ReportConfig = React.useContext(StateConfigContext)
    .stateConfig as ReportConfig;

  const [isSubmitDisabled, setIsSubmitDisabled] = React.useState<boolean>(true);

  //If created_by_user is added by BE after creation
  const isAmend: boolean =
    !!report && !!report.general_information.created_by_user;

  // Use this ref to avoid cloning the object when comparing
  const refReport = React.useRef<ReportValue>();

  const [generalForm, setGeneralForm] =
    React.useState<GeneralInformationValueType>(
      report
        ? initGeneralInfoFromReport(report)
        : getGeneralFormInitialValue(
            stateConfig.general_information,
            caseNumber,
            generalFormAreas
          )
    );

  const [useOfForceForm, setUseOfForceForm] = React.useState<
    UseOfForceValueType | undefined
  >(
    report && report.use_of_force
      ? report.use_of_force
      : getUseOfForceDefaultValue(stateConfig.use_of_force)
  );

  const [citizens, setCitizens] = React.useState<ItemValueType[]>(
    report ? [...report.citizens] : []
  );

  const [complaints, setComplaints] = React.useState<ItemValueType[]>(
    report && report.complaints ? [...report.complaints] : []
  );

  const [investigations, setInvestigations] = React.useState<ItemValueType[]>(
    report && report.investigations ? [...report.investigations] : []
  );

  const [administrative, setAdministrative] = React.useState<ItemValueType[]>(
    report && report.administrative ? [...report.administrative] : []
  );

  const [officers, setOfficers] = React.useState<ItemValueType[]>(
    report && report.officers ? [...report.officers] : []
  );

  const [agencyTags, setAgencyTags] = React.useState<ReportTag[]>(
    report?.tags?.agency ? [...report.tags.agency] : []
  );

  const [isMissingInfoModalOpen, setIsMissingInfoModalOpen] =
    React.useState<boolean>(false);

  const wasSubmittedRef = React.useRef<boolean>(false);
  const refIsSubmitPristine = React.useRef<boolean>(true);

  // Keep the latest callback in a ref so parent re-renders don't trigger the cleanup
  const onCleanUpRef = React.useRef(onCleanUp);
  onCleanUpRef.current = onCleanUp;

  const [validators, setValidators] = React.useState({
    uof: getUOFValidator(
      stateConfig.validations?.use_of_force,
      report && report.citizens ? report.citizens : [],
      report && report.officers ? report.officers : []
    ),
    bystander: getBystanderValidator(
      stateName,
      report && report.citizens ? report.citizens : []
    ),
    sameContactReason: getSameContactReasonValidator(
      stateConfig.validations?.same_contact_reason,
      report && report.citizens ? report.citizens : []
    ),
    uniqueDriver: getUniqueDriverValidator(
      stateConfig.validations?.unique_driver,
      report && report.citizens ? report.citizens : []
    )
  });

  React.useEffect(() => {
    if (refIsSubmitPristine.current && !isSubmitDisabled) {
      refIsSubmitPristine.current = false;
      if (tour?.id === 6) {
        onTourNext({ tourId: 6, step: 2 });
      } else if (tour?.id === 10) {
        onTourNext({ tourId: 10, step: 2 });
      }
    }
  }, [isSubmitDisabled, tour, onTourNext]);

  // Update ref if report prop changes
  React.useEffect(() => {
    if (report) {
      refReport.current = {
        general_information: initGeneralInfoFromReport(report),
        citizens: [...report.citizens],
        use_of_force: report.use_of_force
          ? { ...report.use_of_force }
          : getUseOfForceDefaultValue(stateConfig.use_of_force),
        complaints: report.complaints ? [...report.complaints] : [],
        investigations: report.investigations ? [...report.investigations] : [],
        administrative: report.administrative ? [...report.administrative] : [],
        officers: report.officers ? [...report.officers] : [],
        tags: report.tags
      };
    }
  }, [report, stateConfig]);

  // Validate form on field change to disable submit button
  React.useEffect(() => {
    let currentValue: ReportValue = {
      general_information: generalForm,
      citizens: citizens,
      use_of_force: useOfForceForm,
      complaints: complaints,
      investigations: investigations,
      administrative: administrative,
      officers: officers
    };

    if (agencyTags.length > 0) {
      currentValue.tags = {
        agency: agencyTags
      };
    } else {
      currentValue.tags = null;
    }

    let isValid: boolean = checkFormValid(currentValue, stateConfig, isAmend);

    // When amend, disabled submit if there isn't changes
    if (!isDraft && isValid && isAmend && refReport.current) {
      isValid = !isEqualObject<ReportValue>(refReport.current, currentValue);
    }

    setIsSubmitDisabled(!isValid);
  }, [
    generalForm,
    citizens,
    isAmend,
    stateConfig,
    useOfForceForm,
    complaints,
    investigations,
    officers,
    agencyTags,
    isDraft,
    administrative
  ]);

  //OnCleanUp event to save draft report
  React.useEffect(() => {
    const getContactFormDraft = (): ReportValue => {
      let formSubmit: ReportValue = {
        general_information: {
          ...generalForm,
          date: getDateTime(
            generalForm.date as moment.Moment,
            generalForm.time as moment.Moment
          )
        },
        citizens: [...citizens]
      };

      if (!isUseOfForceEmpty(useOfForceForm)) {
        formSubmit.use_of_force = useOfForceForm;
      }

      formSubmit.officers = officers;

      if (agencyTags.length > 0) {
        formSubmit.tags = {
          agency: agencyTags.map((t) => ({
            id: t.id,
            name: t.name,
            color: t.color
          }))
        };
      }
      return formSubmit;
    };

    return () => {
      if (onCleanUpRef.current) {
        onCleanUpRef.current(getContactFormDraft());
      }
    };
  }, [agencyTags, officers, citizens, generalForm, useOfForceForm]);

  // Update general information form when field change
  const onContactFieldChange = React.useCallback(
    (
      name: string,
      value: FieldValueType | SectionValueType,
      isStateRelated: boolean
    ): void => {
      setGeneralForm((form: GeneralInformationValueType) => {
        const newForm = { ...form };

        if (isStateRelated) {
          newForm.state_related[name] = value as SectionValueType;
        } else {
          newForm[name] = value as FieldValueType;
        }

        return newForm;
      });

      // If the field is "was_use_of_force" and it's value is now false
      // reset the uof form
      if (name === 'was_use_of_force' && !value) {
        setUseOfForceForm(getUseOfForceDefaultValue(stateConfig.use_of_force));
      }
    },
    [stateConfig.use_of_force]
  );

  // Set "was_use_of_force" value if needed
  React.useEffect(() => {
    if (validators.uof.isActive && validators.uof.areCitizensWithUOF) {
      onContactFieldChange('was_use_of_force', true, false);
    }
  }, [validators, onContactFieldChange]);

  const updatedOfficersOnCitizenDelete = (
    officers: ItemValueType[],
    index: number
  ): ItemValueType[] => {
    const newOfficers: ItemValueType[] = officers.map((officer) => {
      if (
        officer.incident_information &&
        officer.incident_information.force_details
      ) {
        const newForceDetails = (
          officer.incident_information.force_details as ItemModelValueType[]
        ).map((force) => {
          const directedAt = force.directed_at as number[] | undefined;
          if (!Array.isArray(directedAt) || directedAt.length === 0) {
            return force;
          }

          const newDirectedAt = directedAt.reduce<number[]>(
            (acc, citizenIdx) => {
              if (citizenIdx === index) return acc;
              acc.push(citizenIdx > index ? citizenIdx - 1 : citizenIdx);
              return acc;
            },
            []
          );

          return {
            ...force,
            directed_at: newDirectedAt
          };
        });

        return {
          ...officer,
          incident_information: {
            ...officer.incident_information,
            force_details: newForceDetails
          }
        };
      }

      return officer;
    });

    return newOfficers;
  };

  const updateOfficersOnCitizenUpdate = (
    officers: ItemValueType[],
    index: number
  ): ItemValueType[] => {
    const newOfficers: ItemValueType[] = officers.map((officer) => {
      if (
        officer.incident_information &&
        officer.incident_information.force_details
      ) {
        const newForceDetails = (
          officer.incident_information.force_details as ItemModelValueType[]
        ).map((force) => {
          const directedAt = force.directed_at as number[] | undefined;
          if (!Array.isArray(directedAt) || directedAt.length === 0) {
            return force;
          }

          const newDirectedAt = directedAt.filter((val) => {
            return val !== index;
          });

          return {
            ...force,
            directed_at: newDirectedAt
          };
        });

        return {
          ...officer,
          incident_information: {
            ...officer.incident_information,
            force_details: newForceDetails
          }
        };
      }

      return officer;
    });

    return newOfficers;
  };

  /**
   *
   * @param newCitizens List of citizens updated
   * @param index position in the list of the citizen updated/removed (OPTIONAL)
   */
  const onCitizensChange = (
    newCitizens: ItemValueType[],
    index?: number // Only for Remove Action
  ): void => {
    let newOfficers: ItemValueType[] = [];

    if (index !== undefined && index !== null) {
      // on REMOVE Action
      if (citizens.length > newCitizens.length && officers.length > 0) {
        newOfficers = updatedOfficersOnCitizenDelete(officers, index);
      } else if (newCitizens.length > 0 && officers.length > 0) {
        // on EDIT Action
        if (
          newCitizens[index] &&
          newCitizens[index].incident_information &&
          newCitizens[index].incident_information.use_of_force !== true
        ) {
          newOfficers = updateOfficersOnCitizenUpdate(officers, index);
        }
      }
    }

    setValidators({
      uof: getUOFValidator(
        stateConfig.validations?.use_of_force,
        newCitizens,
        newOfficers.length > 0 ? newOfficers : officers
      ),
      bystander: getBystanderValidator(stateName, newCitizens),
      sameContactReason: getSameContactReasonValidator(
        stateConfig.validations?.same_contact_reason,
        newCitizens
      ),
      uniqueDriver: getUniqueDriverValidator(
        stateConfig.validations?.unique_driver,
        newCitizens
      )
    });

    if (newOfficers.length > 0) {
      setOfficers(newOfficers);
    }
    setCitizens(newCitizens);
  };

  const onUseOfForceChange = (newUseOfForce: UseOfForceValueType): void => {
    setUseOfForceForm(newUseOfForce);
  };

  const onComplaintsChange = (newComplaints: ItemValueType[]): void => {
    setComplaints(newComplaints);
  };

  const onInvestigationsChange = (newInvestigations: ItemValueType[]): void => {
    setInvestigations(newInvestigations);
  };

  const onAdministrativeChange = (newAdministrative: ItemValueType[]): void => {
    setAdministrative(newAdministrative);
  };

  const onOfficersChange = (newOfficers: ItemValueType[]) => {
    setValidators((prev) => ({
      ...prev,
      uof: getUOFValidator(
        stateConfig.validations?.use_of_force,
        citizens,
        newOfficers
      )
    }));
    setOfficers(newOfficers);
  };

  const onTagsChange = (newTags: ReportTag[]) => {
    setAgencyTags(newTags);
  };

  const onClickSubmit = () => {
    wasSubmittedRef.current = true;
    if (
      !isUOFValid(validators.uof, !!generalForm.was_use_of_force) ||
      !isBystanderValid(validators.bystander) ||
      !isSameContactReasonValid(validators.sameContactReason) ||
      !isUniqueDriverValid(validators.uniqueDriver)
    ) {
      setIsMissingInfoModalOpen(true);
    } else {
      let formSubmit: ReportValue = {
        general_information: {
          ...generalForm,
          date: getDateTime(
            generalForm.date as moment.Moment,
            generalForm.time as moment.Moment
          )
        },
        citizens: [...citizens]
      };

      if (!isUseOfForceEmpty(useOfForceForm)) {
        formSubmit.use_of_force = useOfForceForm;
      }

      if (isAmend) {
        formSubmit.complaints = complaints;
        formSubmit.investigations = investigations;
        formSubmit.administrative = administrative;
      }

      formSubmit.officers = officers;

      if (agencyTags.length > 0) {
        formSubmit.tags = {
          agency: agencyTags.map((t) => ({
            id: t.id,
            name: t.name,
            color: t.color
          }))
        };
      }

      delete formSubmit.general_information['time'];

      onTourEnd();
      onSubmit(formSubmit, tourStatus === 'active');
    }
  };

  const onMissingInformationModalClose = () => {
    setIsMissingInfoModalOpen(false);
  };

  const getCitizensMessage = (): ItemListSectionMessage | undefined => {
    if (citizens.length === 0) {
      return { value: CITIZENS_MIN_MSG, color: 'neutral' };
    } else if (
      wasSubmittedRef.current &&
      validators.uof.isActive &&
      generalForm.was_use_of_force &&
      !validators.uof.areCitizensWithUOF
    ) {
      return { value: CITIZENS_UOF_MIN_MSG, color: 'error' };
    }
  };

  const getOfficersMessage = (): ItemListSectionMessage | undefined => {
    if (citizens.length === 0) {
      return { value: OFFICERS_NEEDS_CITIZEN_MSG, color: 'neutral' };
    } else if (
      validators.uof.isActive &&
      generalForm.was_use_of_force &&
      !validators.uof.areOfficersLinked
    ) {
      return {
        value: OFFICERS_NEEDS_UOF_CITIZEN_MSG,
        color: wasSubmittedRef.current ? 'error' : 'neutral'
      };
    }
  };

  const getMissingInfoMessages = (): string[] => {
    let messages = [];
    if (validators.uof.isActive && generalForm.was_use_of_force) {
      if (!validators.uof.areCitizensWithUOF) {
        messages.push(CITIZENS_UOF_MIN_MSG);
      }

      if (!validators.uof.areOfficersLinked) {
        messages.push(OFFICERS_NEEDS_UOF_CITIZEN_MSG);
      }
    }

    if (validators.bystander.isActive) {
      if (!validators.bystander.isCitizenWithUOF) {
        messages.push(BYSTANDER_NEEDS_UOF_CITIZEN);
      }

      if (!validators.bystander.areBystanderValid) {
        messages.push(BYSTANDER_INVALID);
      }

      if (!validators.bystander.areBystanderNoAction) {
        messages.push(BYSTANDER_NO_ACTION);
      }
    }

    if (validators.sameContactReason.isActive) {
      if (!validators.sameContactReason.areCitizensWithSameContactReason) {
        messages.push(SAME_CONTACT_REASON_INVALID);
      }

      if (
        !validators.sameContactReason
          .areCitizensWithSameContactReasonLPRNotification
      ) {
        messages.push(SAME_CONTACT_REASON_LPR_NOTIFICATION_INVALID);
      }
    }

    if (validators.uniqueDriver.isActive) {
      if (!validators.uniqueDriver.hasUniqueDriver) {
        messages.push(UNIQUE_DRIVER_INVALID);
      }
    }
    return messages;
  };

  return {
    showStateMandated,
    isAmend,
    isMissingInfoModalOpen,
    isSubmitDisabled,
    citizens,
    officers,
    complaints,
    investigations,
    administrative,
    generalForm,
    useOfForceForm,
    agencyTags,
    validators,
    stateConfig,
    tour,
    getMissingInfoMessages,
    getCitizensMessage,
    getOfficersMessage,
    onCitizensChange,
    onContactFieldChange,
    onUseOfForceChange,
    onOfficersChange,
    onComplaintsChange,
    onInvestigationsChange,
    onAdministrativeChange,
    onTagsChange,
    onClickSubmit,
    onMissingInformationModalClose
  };
};
